import { useEffect, useState } from 'react'
import type { Model } from '@byokapp/types'
import { getAdapter } from '@/lib/adapters'

// Module-level cache: serve immediately on re-mount, refresh in background.
// Keyed by providerId — one key per provider in practice.
const modelCache = new Map<string, Model[]>()

export function useProviderModels(
  providerId: string,
  apiKey: string | null,
): { models: Model[]; loading: boolean; error: string | null } {
  const [models, setModels] = useState<Model[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const adapter = getAdapter(providerId)
    if (!adapter) {
      setModels([])
      setLoading(false)
      setError('Unknown provider')
      return
    }

    if (!apiKey) {
      setModels(modelCache.get(providerId) ?? [])
      setLoading(false)
      setError(null)
      return
    }

    const cached = modelCache.get(providerId)
    if (cached) {
      setModels(cached)
      setLoading(false)
    } else {
      setLoading(true)
    }

    let cancelled = false
    adapter
      .listModels(apiKey)
      .then((list) => {
        if (cancelled) return
        modelCache.set(providerId, list)
        setModels(list)
        setLoading(false)
        setError(null)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setLoading(false)
        setError(err instanceof Error ? err.message : 'Failed to load models')
      })

    return () => {
      cancelled = true
    }
  }, [providerId, apiKey])

  return { models, loading, error }
}
